import {func, number} from 'prop-types';
import {useCallback, useEffect, useState} from 'react';

import {useDebounce, useExercisesById} from '../../utils/hooks.js';
import {runAfterAnimations} from '../../utils/misc.js';
import useLocalStore from '../../utils/useLocalStore.js';
import SheetNav from '../sheet/SheetNav.jsx';
import SheetNavButton from '../sheet/SheetNavButton.jsx';
import SheetPage from '../sheet/SheetPage.jsx';
import SheetTitle from '../sheet/SheetTitle.jsx';
import SheetDialog from '../ui/SheetDialog.jsx';
import ExerciseDetailPage from './ExerciseDetailPage.jsx';
import ExerciseFilters from './ExerciseFilters.jsx';
import ExercisesList from './ExercisesList.jsx';
import PositionInfo from './PositionInfo.jsx';

export default function ExercisePickerDialog({
  onRemoveExercise,
  onSelectExercise,
  startingExerciseId,
  startingMuscleGroupId,
  onClose,
  onCustom,
}) {
  const isOpen = useLocalStore(st => st.exerciseChooserOpen);
  const setIsOpen = useLocalStore(st => st.setExerciseChooserOpen);

  const [page, setPage] = useState(startingExerciseId ? 2 : 1);
  const [focusedExerciseId, setFocusedExerciseId] = useState(startingExerciseId);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filters, setFilters] = useState({
    muscleGroupId: startingMuscleGroupId,
    exerciseType: null,
    author: null,
  });

  const [searchText, setSearchText] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const debouncedOnChange = useDebounce(() => setSearchFilter(searchText));

  const handleSearchChange = useCallback(
    value => {
      debouncedOnChange();
      setSearchText(value);
    },
    [debouncedOnChange]
  );

  const handleClearFilters = useCallback(() => {
    setFilters({
      muscleGroupId: null,
      exerciseType: null,
      author: null,
    });
  }, [setFilters]);

  const handleClearSearch = useCallback(() => {
    debouncedOnChange();
    setSearchText('');
  }, [debouncedOnChange]);

  const handleOpen = useCallback(() => {
    if (startingExerciseId) {
      setPage(2);
      setFocusedExerciseId(startingExerciseId);
    }
  }, [startingExerciseId]);

  const handleFocus = useCallback(exerciseId => {
    setFocusedExerciseId(exerciseId);
    setPage(2);
  }, []);

  useEffect(() => {
    if (isOpen) {
      handleOpen();
    }
  }, [handleOpen, isOpen]);

  const exercisesById = useExercisesById();

  const handleClose = useCallback(() => {
    setIsOpen(false);
    runAfterAnimations(() => {
      setPage(1);
      handleClearSearch();
      handleClearFilters();
      setFocusedExerciseId(null);
      setFiltersOpen(false);
      onClose();
    });
  }, [handleClearFilters, handleClearSearch, onClose, setIsOpen]);

  const handleCustomExercise = useCallback(() => {
    handleClose();
    onCustom();
  }, [handleClose, onCustom]);

  const handleSelectExercise = useCallback(
    exerciseId => {
      onSelectExercise(exerciseId);
      handleClose();
    },
    [handleClose, onSelectExercise]
  );

  useEffect(() => {
    if (startingMuscleGroupId || startingExerciseId) {
      setFilters(filters => ({
        ...filters,
        muscleGroupId: startingMuscleGroupId,
        exerciseId: startingExerciseId,
      }));
    }
  }, [startingExerciseId, startingMuscleGroupId]);

  return (
    <SheetDialog isOpen={isOpen} onClose={handleClose}>
      <div className="mt-2">
        <SheetTitle title="Exercises" currentPage={page} pageNumber={1} variant="xl">
          <PositionInfo pageNumber={1} />
        </SheetTitle>

        <SheetTitle
          title={exercisesById[focusedExerciseId]?.name}
          currentPage={page}
          pageNumber={2}
        >
          <PositionInfo pageNumber={2} />
        </SheetTitle>

        <SheetTitle title="Filters" currentPage={page} pageNumber={3} />
      </div>

      <SheetNav handleClose={handleClose}>
        <SheetNavButton
          label="Exercises"
          currentPage={page}
          pageNumber={2}
          onClick={() => setPage(1)}
        />
      </SheetNav>

      <div className="relative h-full desktop:min-h-[700px]">
        <SheetPage currentPage={page} pageNumber={1}>
          <ExercisesList
            pageNumber={1}
            onSelectExercise={handleSelectExercise}
            onRemoveExercise={onRemoveExercise}
            startingExerciseId={startingExerciseId}
            startingMuscleGroupId={startingMuscleGroupId}
            onFocus={handleFocus}
            filters={filters}
            onClearFilters={handleClearFilters}
            onClearSearch={handleClearSearch}
            onSearchChange={handleSearchChange}
            onOpenFilters={() => setFiltersOpen(true)}
            searchValue={searchText}
            searchFilter={searchFilter}
            onCustom={handleCustomExercise}
          />
        </SheetPage>

        <ExerciseDetailPage
          pageNumber={2}
          currentPage={page}
          onSelect={handleSelectExercise}
          onRemoveExercise={onRemoveExercise}
          focusedExerciseId={focusedExerciseId}
        />
      </div>

      <ExerciseFilters
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        filters={filters}
        setFilters={setFilters}
      />
    </SheetDialog>
  );
}

ExercisePickerDialog.propTypes = {
  onClose: func,
  onCustom: func.isRequired,
  onRemoveExercise: func.isRequired,
  onSelectExercise: func.isRequired,
  startingExerciseId: number,
  startingMuscleGroupId: number,
};
